import React, { createContext, useContext, useState, useCallback } from "react";

export interface DetailSideSheetContextType {
  detailContent: React.ReactNode;
  isDetailSideSheetOpen: boolean;
  sideSheetWidth: number;
  shouldDarkenWithDetail: boolean;
  openDetailSideSheet: (
    content: React.ReactNode,
    shouldDarken?: boolean
  ) => void;
  closeDetailSideSheet: () => void;
  setSideSheetWidth: (width: number) => void;
}

const DetailSideSheetContext = createContext<
  DetailSideSheetContextType | undefined
>(undefined);

export const useDetailSideSheet = () => {
  const context = useContext(DetailSideSheetContext);
  if (!context) {
    throw new Error(
      "useDetailSideSheet must be used within a DetailSideSheetProvider"
    );
  }
  return context;
};

interface DetailSideSheetProviderProps {
  children: React.ReactNode;
}

export const DetailSideSheetProvider: React.FC<
  DetailSideSheetProviderProps
> = ({ children }) => {
  const [detailContent, setDetailContent] = useState<React.ReactNode>(null);
  const [isDetailSideSheetOpen, setIsDetailSideSheetOpen] =
    useState<boolean>(false);
  const [sideSheetWidth, setSideSheetWidth] = useState<number>(400);
  const [shouldDarkenWithDetail, setShouldDarkenWithDetail] =
    useState<boolean>(false);

  const openDetailSideSheet = useCallback(
    (content: React.ReactNode, shouldDarken: boolean = false) => {
      setDetailContent(content);
      setIsDetailSideSheetOpen(true);
      setShouldDarkenWithDetail(shouldDarken);
    },
    []
  );

  const closeDetailSideSheet = useCallback(() => {
    setDetailContent(null);
    setIsDetailSideSheetOpen(false);
    setShouldDarkenWithDetail(false);
  }, []);

  const value: DetailSideSheetContextType = {
    detailContent,
    isDetailSideSheetOpen,
    sideSheetWidth,
    shouldDarkenWithDetail,
    openDetailSideSheet,
    closeDetailSideSheet,
    setSideSheetWidth,
  };

  return (
    <DetailSideSheetContext.Provider value={value}>
      {children}
    </DetailSideSheetContext.Provider>
  );
};

export default DetailSideSheetContext;
